// Per-page illustrative images: display, generate pending, regenerate, add new.
// Handles broken assets (missing R2 object) with a placeholder.

import React from "react";
import type { AssetRef } from "@asmg/shared";
import { api } from "../api/client";
import { Button, ErrorBanner } from "../components/ui";

function ImageItem({
  projectId,
  asset,
  onReplace,
}: {
  projectId: string;
  asset: AssetRef;
  onReplace: (a: AssetRef) => void;
}) {
  const [instruction, setInstruction] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<unknown>(null);
  const [broken, setBroken] = React.useState(false);
  const [version, setVersion] = React.useState(0);

  const pending = !asset.hasImage;

  async function run(body: { description?: string; instruction?: string }) {
    setBusy(true);
    setError(null);
    try {
      const updated = await api.generateAssetImage(projectId, asset.id, body);
      onReplace(updated); // prior preserved on failure (server-side)
      setBroken(false);
      setVersion((v) => v + 1);
      setInstruction("");
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      {asset.description && <div className="muted" style={{ marginBottom: 6 }}>🖼 {asset.description}</div>}

      {pending ? (
        <div className="chart-placeholder">Image not generated yet.</div>
      ) : broken ? (
        <div className="chart-placeholder">Image unavailable (asset missing).</div>
      ) : (
        <div className="illus">
          <img
            src={`${api.assetUrl(asset.id)}?v=${version}`}
            alt={asset.description ?? "illustration"}
            onError={() => setBroken(true)}
          />
        </div>
      )}

      <ErrorBanner error={error} onRetry={() => run(pending ? {} : { instruction })} />

      <div className="row" style={{ marginTop: 8 }}>
        {pending ? (
          <Button onClick={() => run({})} loading={busy}>
            Generate Image
          </Button>
        ) : (
          <>
            <input
              className="grow"
              placeholder="Regenerate instruction (optional)"
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
            />
            <Button variant="ghost" onClick={() => run({ instruction: instruction.trim() || undefined })} loading={busy}>
              Regenerate
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export function ImagePanel({
  projectId,
  pageId,
  assets,
  onReplace,
  onAdd,
}: {
  projectId: string;
  pageId: string;
  assets: AssetRef[];
  onReplace: (a: AssetRef) => void;
  onAdd: (a: AssetRef) => void;
}) {
  const [newDesc, setNewDesc] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<unknown>(null);

  async function addImage() {
    const d = newDesc.trim();
    if (!d) return;
    setBusy(true);
    setError(null);
    try {
      const created = await api.generatePageImage(projectId, pageId, d);
      onAdd(created);
      setNewDesc("");
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>Illustrative images</div>
      {assets.map((a) => (
        <ImageItem key={a.id} projectId={projectId} asset={a} onReplace={onReplace} />
      ))}

      <div className="card">
        <label>Add an image to this page</label>
        <div className="row">
          <input
            className="grow"
            placeholder="Describe the diagram/image to generate"
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
          />
          <Button onClick={addImage} loading={busy} disabled={!newDesc.trim()}>
            Generate
          </Button>
        </div>
        <ErrorBanner error={error} onRetry={addImage} />
      </div>
    </div>
  );
}
